import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { getSalonEarnings, salonCancelPaidBooking, salonMarkNoShow } from "../../lib/api";
import { formatAppointment } from "../../lib/datetime";

const aud = (value) => `$${Number(value || 0).toFixed(2)}`;
const BADGE = {
  paid: "bg-espresso text-bone",
  scheduled: "border border-burgundy/40 text-burgundy",
  retry: "border border-burgundy/40 text-burgundy",
  processing: "border border-burgundy/40 text-burgundy",
  awaiting_connect: "border border-mocha/50 text-mocha",
  failed: "bg-burgundy text-bone",
  cancelled: "border border-ink/20 text-ink/45",
};

function Stat({ label, value, testId }) {
  return (
    <div className="border border-sand/70 rounded-xl px-4 py-3 bg-bone">
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink/45">{label}</p>
      <p className="mt-1 font-serif text-2xl text-espresso" data-testid={testId}>{value}</p>
    </div>
  );
}

function EarningRow({ row, busy, onAction }) {
  return (
    <article className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] md:items-center" data-testid={`salon-earning-${row.id}`}>
      <div className="min-w-0">
        <p className="font-serif text-lg text-ink truncate">{row.service}</p>
        <p className="text-xs text-ink/55 truncate">{row.customer_name} · {row.appointment_at ? formatAppointment(row.appointment_at) : row.when}{row.status === "no_show" ? " · No-show" : row.status === "cancelled" ? " · Cancelled" : ""}</p>
      </div>
      <div className="text-sm text-ink/70">
        <p data-testid={`salon-earning-amount-${row.id}`}>{aud(row.amount)} paid by client</p>
        {row.payout_status === "paid" && <p className="text-xs text-ink/50" data-testid={`salon-earning-net-${row.id}`}>{aud(row.payout_amount)} to you · Stripe fee {aud(row.stripe_fee)}</p>}
        {row.refund_amount > 0 && <p className="text-xs text-ink/50">{aud(row.refund_amount)} refunded</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 md:justify-end">
        <span className={`rounded-full px-3 py-1 text-[11px] ${BADGE[row.payout_status] || "border border-sand text-ink/50"}`} data-testid={`salon-earning-status-${row.id}`}>{row.payout_label}</span>
        {row.can_mark_no_show && <button type="button" disabled={busy} onClick={() => onAction(row, "no-show")} className="rounded-full border border-sand px-3 py-1 text-[11px] text-ink/70 hover:border-espresso disabled:opacity-50" data-testid={`salon-earning-noshow-${row.id}`}>Mark no-show</button>}
        {row.can_cancel && <button type="button" disabled={busy} onClick={() => onAction(row, "cancel")} className="rounded-full border border-sand px-3 py-1 text-[11px] text-ink/70 hover:border-burgundy hover:text-burgundy disabled:opacity-50" data-testid={`salon-earning-cancel-${row.id}`}>Cancel &amp; refund</button>}
      </div>
    </article>
  );
}

export const SalonEarnings = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getSalonEarnings().then(setData).catch(() => setData({ rows: [], totals: {}, connect_ready: false })), []);
  useEffect(() => { load(); }, [load]);

  const onAction = async (row, action) => {
    const prompt = action === "cancel" ? `Cancel ${row.customer_name}'s booking? They'll get a full refund of ${aud(row.amount)}.` : `Mark ${row.customer_name} as a no-show? You keep the full amount.`;
    if (!window.confirm(prompt)) return;
    setBusy(true);
    try {
      if (action === "cancel") { await salonCancelPaidBooking(row.id); toast.success("Booking cancelled. The client has been fully refunded."); }
      else { await salonMarkNoShow(row.id); toast.success("Marked as no-show. You keep the full amount."); }
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work. Please try again.");
    } finally { setBusy(false); }
  };

  if (!data) return <section className="mt-12" id="earnings" data-testid="salon-earnings-loading" />;
  const { totals = {}, rows = [] } = data;
  return (
    <section className="mt-12" id="earnings" data-testid="salon-earnings">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-xl text-ink">Earnings</h2>
        <p className="text-xs text-ink/45">Paid after each appointment · Stripe fee only, no FYLLR commission</p>
      </div>
      {!data.connect_ready && <p className="mb-4 text-sm text-mocha border border-mocha/30 rounded-xl px-4 py-3" data-testid="salon-earnings-connect-warning">Connect your Stripe account under Payouts so we can send you your earnings.</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Paid out" value={aud(totals.paid_out)} testId="salon-earnings-paid-out" />
        <Stat label="Pending" value={aud(totals.pending)} testId="salon-earnings-pending" />
        <Stat label="Refunded" value={aud(totals.refunded)} testId="salon-earnings-refunded" />
        <Stat label="Stripe fees" value={aud(totals.stripe_fees)} testId="salon-earnings-fees" />
      </div>
      {rows.length ? (
        <div className="bg-bone border border-sand/70 rounded-2xl divide-y divide-sand/70 overflow-hidden" data-testid="salon-earnings-list">
          {rows.map((row) => <EarningRow key={row.id} row={row} busy={busy} onAction={onAction} />)}
        </div>
      ) : (
        <div className="bg-bone border border-dashed border-sand rounded-2xl p-8 text-center" data-testid="salon-earnings-empty"><p className="font-serif text-xl text-ink">No paid bookings yet.</p><p className="mt-1 text-sm text-ink/55">When clients book and pay for your openings, each payout will be tracked here.</p></div>
      )}
    </section>
  );
};
